import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AnalyticsEntry } from 'app/api/analytics.api.service';
import { CreateRuleEntryInput, DeleteRuleEntryInput, UpdateRuleEntryInput } from 'app/api/API.service';
import { RuleEntry } from 'app/api/rule.api.service';
import { AnalyticsSelectors } from 'app/main/analytics/selectors';
import {
  RuleEntryCreateRequested,
  RuleEntryDeleteRequested,
  RuleEntryUpdateRequested
} from 'app/main/rule/rule.actions';
// tslint:disable-next-line: max-line-length
import { KeyValueCellRendererComponent } from 'app/main/shared/key-value-cell-renderer/key-value-cell-renderer.component';
import { AppState } from 'app/reducers';
import { agGridLocale } from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanySubset } from 'app/api/company.api.service';

export interface RuleRow extends RuleEntry {
  custKeyValue?: string;
}

@Component({
  selector: 'app-customer-rule',
  templateUrl: './customer-rule.component.html',
  styleUrls: ['./customer-rule.component.scss']
})
export class CustomerRuleComponent implements OnInit, OnDestroy, AfterViewInit {
  public gridApi;
  public gridColumnApi;

  public columnDefs;
  public defaultColDef;
  public statusBar;
  public localeText;
  public modules;
  public frameworkComponents;
  public editType = '';
  public rowSelection = 'single';
  public getRowNodeId;
  public deltaRowDataMode = true;

  selectedRow$: BehaviorSubject<RuleRow>;
  rowData$: Observable<RuleRow[]>;

  private _search = '';

  @Input() company: CompanySubset;
  @Input() 
  set search(value: string) {
    this._search = value;
    if (this.gridApi) {
      this.gridApi.setQuickFilter(value);      
    }
  }

  @ViewChild('agGridRule', { static: false }) agGrid: AgGridAngular;

  private custList: AnalyticsEntry[];

  // Protected
  protected logger: any;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    protected amplifyService: AmplifyService,
    protected store: Store<AppState>,
  ) {
    this.logger = this.amplifyService.logger('CustomerRuleComponent');
    this._unsubscribeAll = new Subject();
    this.selectedRow$ = new BehaviorSubject(null);
    this.modules = AllModules;
    this.localeText = agGridLocale.fr;
    this.columnDefs = [
      { headerName: 'Rechercher le texte', field: 'name', minWidth: 200, width: 200, editable: true },
      {
        headerName: 'Dans',
        field: 'target',
        minWidth: 150,
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: ['account', 'entryLabel'],
        },
        valueFormatter: params => params.value === 'account' ? 'Compte' : 'Libellé'
      },
      {
        headerName: 'Affecter',
        field: 'custKeyValue',
        minWidth: 200,
        width: 200,
        editable: true,
        cellRenderer: 'keyValueCellRendererComponent',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: params => this.cellEditorParams(params)
      }, // Select
    ];
    this.defaultColDef = {
      enableValue: true,
      sortable: true,
      filter: true,
      resizable: true
    };
    this.statusBar = {
      statusPanels: [
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'left'
        },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agFilteredRowCountComponent' }
      ]
    };
    this.frameworkComponents = { keyValueCellRendererComponent: KeyValueCellRendererComponent };
    this.getRowNodeId = data => data.key;
  }

  ngOnInit(): void {
    this.rowData$ = this.store.pipe(select(AnalyticsSelectors.selectFlatCustomerRule));

    this.store
    .pipe(
        takeUntil(this._unsubscribeAll),
        select(AnalyticsSelectors.selectCust),
    )
    .subscribe(entries => {
      this.custList = entries;
    });
  }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onGridReady(params: { api: any; columnApi: any; }): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.setQuickFilter(this._search);      
  }

  onFirstDataRendered(params): void {
    this.autoSizeAll(params);
  }

  onCellValueChanged(ev: any): void {
    const data = ev.data as RuleRow;
    if (data && data.key && data.name) {
      const anaKey = data.custKeyValue ? data.custKeyValue.split('|')[0] : null;
      const input: UpdateRuleEntryInput = {
        companyId: data.companyId,
        key: data.key,
        name: data.name,
        target: data.target,
        anaKey,
      };
      this.store.dispatch(new RuleEntryUpdateRequested({ input }));
    }
  }

  onSelectionChanged(): void {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      this.selectedRow$.next(selectedRows[0]);
    } else {
      this.selectedRow$.next(null);
    }
  }

  onRowDataUpdated(ev: any): void {
    this.gridApi.forEachNode(node => {
      if (node.data.name === '-- Nouveau --') {
        node.setSelected(true);
        this.gridApi.startEditingCell({
          rowIndex: node.rowIndex,
          colKey: 'name'
        });
      }
    });
  }

  onCreate(): void {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      const row = selectedRows[0] as RuleRow;
      this.store.dispatch(new RuleEntryCreateRequested({
        input: {
          companyId: row.companyId,
          axis: row.axis,
          name: '-- Nouveau --',
          target: row.target,
        } as CreateRuleEntryInput
      }));
      this.selectedRow$.next(null);
    } else if (this.company) {
      this.store.dispatch(new RuleEntryCreateRequested({
        input: {
          companyId: this.company.id,
          axis: 'cust',
          name: '-- Nouveau --',
          target: 'entryLabel',
        } as CreateRuleEntryInput
      }));
    }
  }

  onDelete(): void {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      const row = selectedRows[0] as RuleRow;
      this.store.dispatch(new RuleEntryDeleteRequested({
        input: {
          companyId: row.companyId,
          key: row.key,
        } as DeleteRuleEntryInput
      }));
      this.selectedRow$.next(null);
    }
  }

  cellEditorParams(params): any {
    if (!this.custList) { return { values: [] }; }
    const values = this.custList.map((entry: AnalyticsEntry) => `${entry.key}|${entry.name}`);
    return { values, cellRenderer: 'keyValueCellRendererComponent' };                    
  }

  private autoSizeAll(params: any): void {
    const allColumnIds = params.columnApi
      .getAllColumns()
      .map(column => column.colId);
    params.columnApi.autoSizeColumns(allColumnIds);
  }

}
